// Notification hub context: owns the unread-count badge shown on the bell and
// reacts to foreground push receipts. Kept minimal — the full list + filters
// live in useNotifications. Mounted high in the tree so the Navbar bell and the
// notifications screen share one unread count.

import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import i18n from "i18next";
import { useApiWithAuth } from "../../hooks/useAuth.js";
import { showToast } from "../../shared/appToast.js";
import { getUnreadCount } from "./api.js";
import { isMockNotificationsEnabled } from "./mockConfig.js";
import {
  NOTIFICATION_RECEIVED_EVENT,
  type NotificationReceivedDetail,
} from "./events.js";

interface NotificationsContextValue {
  unreadCount: number;
  refreshUnreadCount: () => Promise<void>;
  /** Optimistically lower the badge after marking one/some read. */
  decrementUnread: (by?: number) => void;
  /** Optimistically clear the badge after marking all read. */
  resetUnread: () => void;
}

const NotificationsContext = createContext<
  NotificationsContextValue | undefined
>(undefined);

export function useNotificationsContext(): NotificationsContextValue {
  const context = useContext(NotificationsContext);
  if (!context) {
    throw new Error(
      "useNotificationsContext must be used within a NotificationsProvider",
    );
  }
  return context;
}

export const NotificationsProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const { apiCall, isAuthenticated } = useApiWithAuth();
  const [unreadCount, setUnreadCount] = useState(0);

  // useApiWithAuth returns a fresh apiCall each render; hold it in a ref so the
  // callbacks below stay referentially stable and don't re-fire their effects.
  const apiCallRef = useRef(apiCall);
  apiCallRef.current = apiCall;

  const refreshUnreadCount = useCallback(async () => {
    if (!isAuthenticated && !isMockNotificationsEnabled()) {
      setUnreadCount(0);
      return;
    }
    try {
      const count = await getUnreadCount(apiCallRef.current);
      setUnreadCount(count);
    } catch {
      // Inbox API may not be reachable yet (parallel dev); leave the badge as-is.
    }
  }, [isAuthenticated]);

  const decrementUnread = useCallback((by = 1) => {
    setUnreadCount((prev) => Math.max(0, prev - by));
  }, []);

  const resetUnread = useCallback(() => setUnreadCount(0), []);

  // Initial + on auth change.
  useEffect(() => {
    void refreshUnreadCount();
  }, [refreshUnreadCount]);

  // Low-frequency foreground refresh so the bell tracks server state without
  // hammering the API. Paused while hidden; refetches on regaining focus.
  useEffect(() => {
    const interval = setInterval(() => {
      if (document.visibilityState === "visible") void refreshUnreadCount();
    }, 60_000);
    const onVisible = () => {
      if (document.visibilityState === "visible") void refreshUnreadCount();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisible);
    };
  }, [refreshUnreadCount]);

  // Foreground push receipt: show an in-app banner and bump the bell.
  useEffect(() => {
    const handler = (event: Event) => {
      const detail = (event as CustomEvent<NotificationReceivedDetail>).detail;
      if (detail?.title || detail?.body) {
        showToast({
          variant: "info",
          title: detail.title || i18n.t("features:notifications.newNotification"),
          message: detail.body || "",
        });
      }
      void refreshUnreadCount();
    };
    window.addEventListener(NOTIFICATION_RECEIVED_EVENT, handler);
    return () =>
      window.removeEventListener(NOTIFICATION_RECEIVED_EVENT, handler);
  }, [refreshUnreadCount]);

  return (
    <NotificationsContext.Provider
      value={useMemo(
        () => ({
          unreadCount,
          refreshUnreadCount,
          decrementUnread,
          resetUnread,
        }),
        [unreadCount, refreshUnreadCount, decrementUnread, resetUnread],
      )}
    >
      {children}
    </NotificationsContext.Provider>
  );
};
